import React from 'react';
import { Box, Typography, Tooltip, Zoom } from '@mui/material';
import { Hub as HubIcon } from '@mui/icons-material';
import { useAppSelector } from '../../store';
import { AGENTS, AgentId } from '../../types';

interface AgentCardProps {
  icon: string;
  name: string;
  status: 'idle' | 'active' | 'completed' | 'monitoring' | 'reviewing';
  isActive: boolean;
  description: string;
  agentColor: string;
}

const AgentCard: React.FC<AgentCardProps> = ({ icon, name, status, isActive, description, agentColor }) => {
  const statusLabel =
    status === 'reviewing' ? 'Review Required' :
      isActive ? 'Active' :
        status === 'completed' ? 'Completed' :
          status === 'monitoring' ? 'Monitoring' : 'Idle';

  const getStatusColor = () => {
    if (status === 'reviewing') return '#3b82f6'; // Blue
    if (isActive) return '#f57c00'; // Toyota Orange
    if (status === 'completed') return '#10b981'; // Emerald
    return '#94a3b8'; // Slate
  };

  const statusColor = getStatusColor();

  return (
    <Tooltip
      title={description}
      placement="top"
      arrow
      TransitionComponent={Zoom}
      enterDelay={400}
    >
      <Box
        sx={{
          background: status === 'reviewing'
            ? 'rgba(239, 246, 255, 0.7)'
            : isActive
              ? 'rgba(255, 243, 224, 0.7)'
              : 'rgba(255, 255, 255, 0.4)',
          backdropFilter: 'blur(10px) saturate(180%)',
          WebkitBackdropFilter: 'blur(10px) saturate(180%)',
          border: '1px solid',
          borderColor: status === 'reviewing'
            ? 'rgba(59, 130, 246, 0.3)'
            : isActive
              ? 'rgba(245, 124, 0, 0.3)'
              : 'rgba(226, 232, 240, 0.3)',
          borderRadius: '12px',
          padding: '10px 8px',
          textAlign: 'center',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          cursor: 'help',
          boxShadow: isActive || status === 'reviewing'
            ? `0 8px 16px -4px ${status === 'reviewing' ? 'rgba(59, 130, 246, 0.15)' : 'rgba(245, 124, 0, 0.15)'}`
            : '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
          '&:hover': {
            transform: 'translateY(-2px) scale(1.02)',
            background: 'rgba(255, 255, 255, 0.8)',
            borderColor: statusColor,
          },
        }}
      >
        <Box
          sx={{
            fontSize: 22,
            marginBottom: '6px',
            color: isActive || status === 'reviewing' ? statusColor : agentColor,
            opacity: isActive || status === 'reviewing' ? 1 : 0.45,
            transition: 'all 0.4s ease',
            display: 'flex',
            justifyContent: 'center',
            filter: isActive || status === 'reviewing' ? 'drop-shadow(0 0 4px rgba(0,0,0,0.1))' : 'none'
          }}
        >
          <i className={`fa-solid ${icon}`} />
        </Box>
        <Typography
          sx={{
            fontSize: '11px',
            fontWeight: 800,
            color: '#0f172a',
            lineHeight: 1.2,
            marginBottom: '2px',
            textTransform: 'uppercase',
            letterSpacing: '0.02em'
          }}
        >
          {name}
        </Typography>
        <Typography
          sx={{
            fontSize: '8px',
            color: statusColor,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            fontWeight: 900,
          }}
        >
          {statusLabel}
        </Typography>
      </Box>
    </Tooltip>
  );
};

const AGENT_DESCRIPTIONS: Record<AgentId, string> = {
  orchestrator: "Routes scenario payload and monitors the global execution state.",
  strategist: "Analyzes market shocks and intel to define high-level supply chain recipes.",
  constraint: "Evaluates regional bottlenecks, inventory health, and policy overrides.",
  optimizer: "Executes high-precision mathematical models to allocate units across the network.",
  compiler: "Synthesizes tactical results into executive narratives and risk summaries."
};

// Manual coordinate layout for a more informative, spread-out "War Room" feel
const POSITIONS: Record<AgentId, { x: number, y: number }> = {
  orchestrator: { x: 0, y: -130 }, // Top
  strategist: { x: -165, y: -10 }, // Mid Left (more spread)
  constraint: { x: 165, y: -10 }, // Mid Right (more spread)
  optimizer: { x: -115, y: 130 }, // Bottom Left
  compiler: { x: 115, y: 130 }, // Bottom Right
};

const AgentOrchestrationNetwork: React.FC = () => {
  const { currentNode, agentStatuses, isRunning, pendingCheckpoint } = useAppSelector(
    (state) => state.analysis
  );

  const isAgentActive = (agentId: AgentId): boolean => {
    if (!currentNode) return false;
    const nodeLower = currentNode.toLowerCase();

    switch (agentId) {
      case 'orchestrator':
        return nodeLower === 'orchestrator' || nodeLower === 'system' || nodeLower === 'router' || nodeLower === 'watchdog';
      case 'strategist':
        return nodeLower === 'strategist' || nodeLower === 'strategy' || nodeLower === 'director';
      case 'constraint':
        return nodeLower === 'constraint_advisor' || nodeLower === 'constraint' || nodeLower === 'analyst' || nodeLower === 'discovery';
      case 'optimizer':
        return nodeLower === 'distribution_optimizer' || nodeLower === 'optimizer' || nodeLower === 'solver';
      case 'compiler':
        return nodeLower === 'insight_compiler' || nodeLower === 'compiler' || nodeLower === 'narrator';
      default:
        return false;
    }
  };

  const getAgentStatus = (agentId: AgentId): 'idle' | 'active' | 'completed' | 'monitoring' | 'reviewing' => {
    // Only show 'reviewing' if it's actually the current node waiting for input
    if (pendingCheckpoint && currentNode === agentId) return 'reviewing';
    if (isAgentActive(agentId)) return 'active';

    const status = agentStatuses[agentId];
    if (status === 'completed') return 'completed';
    if (status === 'active') return 'active';
    if (agentId === 'orchestrator' && isRunning) return 'monitoring';

    return 'idle';
  };

  return (
    <Box
      sx={{
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        borderRadius: '16px',
        border: '1px solid #e2e8f0',
        overflow: 'hidden',
        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
        minHeight: '480px', // Tightened for symmetry
        display: 'flex',
        flexDirection: 'column',
        flex: '0 0 auto',
      }}
    >
      <Box
        sx={{
          background: 'rgba(255, 255, 255, 0.8)',
          padding: '12px 16px',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flexShrink: 0
        }}
      >
        <HubIcon sx={{ color: '#f57c00', fontSize: 18, opacity: 0.8 }} />
        <Typography
          sx={{
            fontSize: '13px',
            fontWeight: 800,
            color: '#334155',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}
        >
          Agent Orchestration Network
        </Typography>
      </Box>

      <Box
        sx={{
          flexGrow: 1, // Fill available space
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: '#fff',
          backgroundImage: 'radial-gradient(#cbd5e1 0.5px, transparent 0.5px)',
          backgroundSize: '24px 24px',
          minHeight: '400px'
        }}
      >
        {/* SVG Connectivity Layer */}
        <svg
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none'
          }}
        >
          <defs>
            <linearGradient id="lineGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#f57c00" stopOpacity="0.2" />
              <stop offset="50%" stopColor="#f57c00" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#f57c00" stopOpacity="0.2" />
            </linearGradient>
            <filter id="glow">
              <feGaussianBlur stdDeviation="2.5" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {AGENTS.map((agent) => {
            const pos = POSITIONS[agent.id];
            // Coordinate mapping for SVG (scaling relative offsets to SVG viewspace)
            const x2 = 50 + (pos.x / 4.5);
            const y2 = 50 + (pos.y / 5);
            const isActive = isAgentActive(agent.id) || getAgentStatus(agent.id) === 'reviewing';

            return (
              <g key={`link-${agent.id}`}>
                <line
                  x1="50%"
                  y1="50%"
                  x2={`${x2}%`}
                  y2={`${y2}%`}
                  stroke="#e2e8f0"
                  strokeWidth="1.5"
                  strokeDasharray="4 4"
                />
                {isActive && (
                  <line
                    className="pulse-line"
                    x1="50%"
                    y1="50%"
                    x2={`${x2}%`}
                    y2={`${y2}%`}
                    stroke="#f57c00"
                    strokeWidth="2.5"
                    strokeDasharray="20 100"
                    pathLength="100"
                    filter="url(#glow)"
                  >
                    <animate
                      attributeName="stroke-dashoffset"
                      from="120"
                      to="0"
                      dur="1.7s"
                      repeatCount="indefinite"
                    />
                  </line>
                )}
              </g>
            );
          })}
        </svg>

        {/* Central Hub Junction */}
        <Box
          sx={{
            position: 'absolute',
            width: '44px',
            height: '44px',
            borderRadius: '50%',
            backgroundColor: '#fff',
            border: '2px solid #f57c00',
            boxShadow: '0 0 20px rgba(245, 124, 0, 0.2)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10,
            '&::after': {
              content: '""',
              position: 'absolute',
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              border: '1px solid #f57c00',
              animation: isRunning ? 'ripple 2s infinite' : 'none',
            },
            '@keyframes ripple': {
              '0%': { transform: 'scale(1)', opacity: 0.8 },
              '100%': { transform: 'scale(2)', opacity: 0 }
            }
          }}
        >
          <HubIcon sx={{ color: '#f57c00', fontSize: 18 }} />
        </Box>

        {AGENTS.map((agent) => {
          const pos = POSITIONS[agent.id];

          return (
            <Box
              key={agent.id}
              sx={{
                position: 'absolute',
                transform: `translate(${pos.x}px, ${pos.y}px)`,
                width: '105px',
                zIndex: 20,
              }}
            >
              <AgentCard
                icon={agent.icon}
                name={agent.label}
                status={getAgentStatus(agent.id)}
                isActive={isAgentActive(agent.id)}
                description={AGENT_DESCRIPTIONS[agent.id]}
                agentColor={agent.color}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default AgentOrchestrationNetwork;
